import React, { useEffect } from 'react';
import { WifiOff } from 'lucide-react';
import { OFFLINE_CONSTANTS } from '@/constants/offline';
import { Button } from '@/components/ui/button';
import { useOfflineStatus } from '@/contexts/OfflineContext';
import { useNavigate } from 'react-router-dom';

/**
 * Offline page that is displayed when the user is offline
 * Provides a fallback UI with useful information and actions
 */
const OfflinePage = () => {
  const { isOnline } = useOfflineStatus();
  const navigate = useNavigate();
  
  // Redirect to home if the user is back online
  useEffect(() => {
    if (isOnline) {
      navigate('/', { replace: true });
    }
  }, [isOnline, navigate]);
  
  const handleRetryConnection = () => {
    window.location.reload();
  };
  
  // Get saved offline data from localStorage if available
  const getOfflineData = () => {
    try {
      const savedData = localStorage.getItem(OFFLINE_CONSTANTS.OFFLINE_DATA_KEY);
      if (savedData) {
        return JSON.parse(savedData);
      }
    } catch (error) {
      
    }
    return null;
  };
  
  const offlineData = getOfflineData();
  
  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4 sm:px-6 lg:px-8 flex flex-col items-center">
      <div className="text-center max-w-md w-full mx-auto">
        <div className="mx-auto h-24 w-24 rounded-full bg-gray-100 flex items-center justify-center mb-8">
          <WifiOff className="h-12 w-12 text-gray-500" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {OFFLINE_CONSTANTS.OFFLINE_PAGE_TITLE}
        </h1>
        
        <p className="text-lg text-gray-600 mb-8">
          {OFFLINE_CONSTANTS.OFFLINE_PAGE_MESSAGE}
        </p>
        
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            {OFFLINE_CONSTANTS.OFFLINE_AVAILABLE_TITLE}
          </h2>
          
          {offlineData?.medications?.length > 0 ? (
            <div className="text-left">
              <p className="mb-3 text-gray-600">Your medication schedule:</p>
              <ul className="list-disc pl-5 space-y-2">
                {offlineData.medications.map((med: any, idx: number) => (
                  <li key={idx} className="text-gray-700">
                    {med.name} - {med.dosage} 
                    {med.time && <span className="text-gray-500 text-sm ml-1">({med.time})</span>}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-gray-600 italic">No medication data available offline</p>
          )}
        </div>
        
        <Button 
          onClick={handleRetryConnection}
          className="w-full bg-gray-600 hover:bg-gray-700 text-white"
        >
          {OFFLINE_CONSTANTS.OFFLINE_RETRY_BUTTON}
        </Button>
        
        <p className="mt-4 text-sm text-gray-500">
          Meduler will automatically reconnect when your connection is restored.
        </p>
      </div>
    </div>
  );
};

export default OfflinePage;
